"use client";

import FAIcon from "@/components/icons/faIcon";
import countryOptions from "@/data/countries.json";
import { isValidEmail } from "@/lib/misc";
import { pushLeadConversion } from "@/lib/analytics";
import { getAttributionForSubmission } from "@/lib/attribution";
import dynamic from "next/dynamic";
import { useEffect, useRef, useState } from "react";
import SectionEyebrow from "../common/sectionEyebrow";
import SectionTitle from "../common/sectionTitle";
import BlurFadeText from "../common/blurFadeText";

const Recaptcha = dynamic(() => import("@/components/common/RecaptchaClient"), {
  ssr: false,
});

const inputClass =
  "w-full rounded-sm border border-black/24 bg-white px-4 py-3.5 text-[1rem] text-[#06040c] placeholder:text-[#06040c]/38 outline-none transition-colors duration-200 focus:border-primary focus:ring-2 focus:ring-primary/15";
const countryTriggerClass = `${inputClass} flex items-center justify-between gap-3 text-left`;
const countryMenuClass =
  "absolute left-0 right-0 top-[calc(100%+0.5rem)] z-30 max-h-80 overflow-y-auto rounded-sm border border-black/24 bg-white shadow-[0_18px_40px_rgba(6,4,12,0.12)] overscroll-contain";
const countryOptionClass =
  "w-full px-4 py-3 text-left text-[1rem] leading-6 text-[#06040c] transition-colors duration-150 hover:bg-black/[0.035] focus:bg-black/[0.035] focus:outline-none";
const countryOptionSelectedClass =
  "bg-primary text-white hover:bg-primary focus:bg-primary";
const phoneFieldShellClass =
  "flex w-full overflow-hidden rounded-sm border border-black/24 transition-colors duration-200 focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/15";
const phonePrefixClass =
  "flex shrink-0 items-center border-r border-black/24 bg-black/[0.04] px-4 text-[1rem] text-[#06040c]/68";
const phoneInputClass =
  "min-w-0 flex-1 bg-white px-4 py-3.5 text-[1rem] text-[#06040c] placeholder:text-[#06040c]/38 outline-none";

const defaultCountryOption =
  countryOptions.find((option) => option.isoCode === "GB") ?? countryOptions[0];

const WEBSITE_ENQUIRY_SUBJECT = "Website Enquiry";
const PLATFORM_DEMO_SUBJECT = "Platform Demo Request";

const normaliseLeadFormType = (value) =>
  value === PLATFORM_DEMO_SUBJECT
    ? PLATFORM_DEMO_SUBJECT
    : WEBSITE_ENQUIRY_SUBJECT;

const isVisibleSource = (source) =>
  String(source?.source_name ?? "").trim().toLowerCase() !== "test";

const getCountryOption = (countryCode) =>
  countryOptions.find((option) => option.isoCode === countryCode) ??
  defaultCountryOption;

const buildPhoneNumber = (dialCode, localNumber) => {
  const trimmedLocalNumber = localNumber.trim();

  return trimmedLocalNumber ? `${dialCode} ${trimmedLocalNumber}` : "";
};

const removeDialCodePrefix = (value, dialCode) => {
  const trimmedValue = value.trimStart();

  if (trimmedValue.startsWith(dialCode)) {
    return trimmedValue
      .slice(dialCode.length)
      .replace(/^[-\s]+/, "")
      .trimStart();
  }

  return trimmedValue.replace(/^[-\s]+/, "");
};

const initialFields = {
  firstName: "",
  lastName: "",
  countryCode: defaultCountryOption.isoCode,
  phoneNumber: "",
  email: "",
  companyName: "",
  sourceId: "",
  referralSource: "",
  marketingConsent: false,
};

const recaptchaVerifyTimeoutMs = 25000;
const contactRequestTimeoutMs = 30000;

const hasEnoughPhoneDigits = (value) => value.replace(/\D/g, "").length >= 7;

export default function FullContactForm({
  embedded = false,
  sectionId = "contact-form",
  sectionClassName = "",
  subject = WEBSITE_ENQUIRY_SUBJECT,
  eyebrow = "Get in touch",
  title = "Enquire about your safety management requirements",
  introDescription = "",
  formTitle = "Complete the form below",
  formDescription = "Fill in the form and we will connect you with the right consultant to discuss your safety management needs",
  successTitle = "Enquiry received.",
  successDescription = "Thanks for sending your details. The team will review the enquiry and come back to you within one working day.",
  submitLabel = "Submit enquiry",
  resetLabel = "Start another enquiry",
}) {
  const recaptchaSiteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY?.trim();
  const [fields, setFields] = useState(initialFields);
  const [sourceOptions, setSourceOptions] = useState([]);
  const [sourceStatus, setSourceStatus] = useState("loading");
  const [status, setStatus] = useState("idle");
  const [errorMessage, setErrorMessage] = useState("");
  const [isCountryMenuOpen, setIsCountryMenuOpen] = useState(false);
  const [isSourceMenuOpen, setIsSourceMenuOpen] = useState(false);
  const recaptchaRef = useRef(null);
  const contactRequestRef = useRef(null);
  const isMountedRef = useRef(true);
  const verifyTimeoutRef = useRef(null);
  const countryMenuRef = useRef(null);
  const sourceMenuRef = useRef(null);
  const selectedCountry = getCountryOption(fields.countryCode);
  const selectedSource = sourceOptions.find(
    (option) => String(option.source_id) === fields.sourceId,
  );
  const selectedCountryLabel =
    selectedCountry.displayLabel ?? selectedCountry.label;
  const selectedSourceLabel =
    sourceStatus === "loading"
      ? "Loading enquiry sources..."
      : sourceStatus === "error"
        ? "Unable to load enquiry sources"
        : sourceStatus === "empty"
          ? "No enquiry sources available"
          : (selectedSource?.source_name ?? "How did you hear about us?");
  const countryListboxId = `${sectionId}-country-listbox`;
  const sourceListboxId = `${sectionId}-source-listbox`;
  const fullPhoneNumber = buildPhoneNumber(
    selectedCountry.dialCode,
    fields.phoneNumber,
  );
  const baseSectionClassName = embedded
    ? "relative w-full overflow-hidden rounded-lg py-0"
    : "relative mt-34 w-full overflow-hidden rounded-lg";
  const shellClassName = embedded
    ? "relative mx-auto flex w-full justify-center px-4 py-4 sm:px-5 sm:py-5 lg:px-6 lg:py-6"
    : "relative mx-auto flex w-full max-w-200 justify-center px-4 py-14 sm:px-6 sm:py-16 lg:px-8 lg:py-18";
  const successClassName = embedded
    ? "flex min-h-[560px] w-full max-w-5xl flex-col items-center justify-center gap-5 rounded-[1.4rem] bg-white/92 px-8 py-12 text-center shadow-[0_18px_60px_rgba(6,4,12,0.12)] sm:px-12"
    : "flex min-h-[620px] w-full max-w-5xl flex-col items-center justify-center gap-5 rounded-lg bg-white/90 px-8 py-12 text-center sm:px-12";
  const formCardClassName = embedded
    ? "w-full max-w-3xl rounded-[1.4rem] border border-white/70 bg-white p-6 backdrop-blur-md shadow-[0_18px_60px_rgba(6,4,12,0.12)] sm:p-8 lg:p-9"
    : "w-full max-w-3xl rounded-lg bg-white p-6 backdrop-blur-md sm:p-8 lg:p-10";
  const introTitleClassName = embedded
    ? "font-raleway text-[2.35rem] font-semibold leading-[0.92] tracking-[-0.065em] text-black sm:text-[2.85rem]"
    : "font-raleway text-[3.15rem] font-semibold leading-[0.92] tracking-[-0.065em] text-black";
  const introBodyClassName = embedded
    ? "text-sm leading-6 text-[#06040c]/62 sm:text-[0.98rem] sm:leading-7"
    : "text-base leading-7 text-[#06040c]/62 sm:text-[1.02rem] sm:leading-8";
  const sectionClasses = [baseSectionClassName, sectionClassName]
    .filter(Boolean)
    .join(" ");

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;

      if (verifyTimeoutRef.current) {
        clearTimeout(verifyTimeoutRef.current);
        verifyTimeoutRef.current = null;
      }

      contactRequestRef.current?.abort?.();
      contactRequestRef.current = null;

      recaptchaRef.current = null;
    };
  }, []);

  useEffect(() => {
    const abortController = new AbortController();

    const loadSources = async () => {
      try {
        setSourceStatus("loading");

        const response = await fetch("/api/contact/sources", {
          method: "GET",
          cache: "no-store",
          signal: abortController.signal,
        });

        if (!response.ok) {
          throw new Error("Failed to load sources");
        }

        const payload = await response.json();
        const nextSources = Array.isArray(payload)
          ? payload.filter(isVisibleSource)
          : [];

        setSourceOptions(nextSources);
        setSourceStatus(nextSources.length > 0 ? "ready" : "empty");
      } catch (error) {
        if (abortController.signal.aborted) {
          return;
        }

        setSourceOptions([]);
        setSourceStatus("error");
      }
    };

    loadSources();

    return () => {
      abortController.abort();
    };
  }, []);

  useEffect(() => {
    const handleVisibility = () => {
      if (!document.hidden) {
        return;
      }

      try {
        recaptchaRef.current?.reset?.();
      } catch {}
    };

    const handlePageHide = () => {
      try {
        recaptchaRef.current?.reset?.();
      } catch {}
    };

    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("pagehide", handlePageHide);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("pagehide", handlePageHide);
    };
  }, []);

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (!countryMenuRef.current?.contains(event.target)) {
        setIsCountryMenuOpen(false);
      }

      if (!sourceMenuRef.current?.contains(event.target)) {
        setIsSourceMenuOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsCountryMenuOpen(false);
        setIsSourceMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const clearVerifyTimeout = () => {
    if (verifyTimeoutRef.current) {
      clearTimeout(verifyTimeoutRef.current);
      verifyTimeoutRef.current = null;
    }
  };

  const handleChange = (event) => {
    const { name, type, checked, value } = event.target;
    const nextValue =
      type === "checkbox"
        ? checked
        : name === "phoneNumber"
          ? removeDialCodePrefix(value, selectedCountry.dialCode)
          : value;

    if (status === "error") {
      setStatus("idle");
      setErrorMessage("");
    }

    setFields((prev) => ({
      ...prev,
      [name]: nextValue,
    }));
  };

  const handleCountryChange = (countryCode) => {
    if (status === "error") {
      setStatus("idle");
      setErrorMessage("");
    }

    setIsCountryMenuOpen(false);

    setFields((prev) => ({
      ...prev,
      countryCode,
    }));
  };

  const handleSourceChange = (sourceId) => {
    if (status === "error") {
      setStatus("idle");
      setErrorMessage("");
    }

    setIsSourceMenuOpen(false);

    setFields((prev) => ({
      ...prev,
      sourceId: String(sourceId),
    }));
  };

  const resetForm = () => {
    clearVerifyTimeout();

    try {
      recaptchaRef.current?.reset?.();
    } catch {}

    setIsCountryMenuOpen(false);
    setIsSourceMenuOpen(false);
    setFields(initialFields);
    setErrorMessage("");
    setStatus("idle");
  };

  const isFormValid = Boolean(
    fields.firstName.trim() &&
    isValidEmail(fields.email.trim()) &&
    (!fields.phoneNumber.trim() || hasEnoughPhoneDigits(fullPhoneNumber)),
  );
  const isSubmitting = status === "verifying" || status === "sending";
  const normalizedFormType = normaliseLeadFormType(subject);

  const buildPayload = (recaptchaToken) => ({
    firstName: fields.firstName.trim(),
    lastName: fields.lastName.trim(),
    country: selectedCountry.label,
    countryCode: selectedCountry.isoCode,
    phoneNumber: fullPhoneNumber,
    email: fields.email.trim(),
    companyName: fields.companyName.trim(),
    sourceId: fields.sourceId ? Number(fields.sourceId) : undefined,
    referralSource: selectedSource?.source_name ?? fields.referralSource.trim(),
    marketingConsent: fields.marketingConsent,
    subject: normalizedFormType,
    formType: normalizedFormType,
    pageSource: sectionId,
    recaptchaToken,
    attribution: getAttributionForSubmission(),
  });

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!isFormValid) {
      return;
    }

    if (!recaptchaSiteKey || !recaptchaRef.current) {
      setStatus("config-error");
      return;
    }

    setStatus("verifying");
    setErrorMessage("");
    clearVerifyTimeout();
    verifyTimeoutRef.current = window.setTimeout(() => {
      if (!isMountedRef.current) {
        return;
      }

      setErrorMessage("reCAPTCHA verification timed out. Please try again.");
      setStatus((currentStatus) =>
        currentStatus === "verifying" ? "error" : currentStatus,
      );

      try {
        recaptchaRef.current?.reset?.();
      } catch {}
    }, recaptchaVerifyTimeoutMs);

    try {
      recaptchaRef.current?.execute?.();
    } catch {
      clearVerifyTimeout();
      setErrorMessage("Unable to start reCAPTCHA verification.");
      setStatus("error");
      try {
        recaptchaRef.current?.reset?.();
      } catch {}
    }
  };

  const onReCAPTCHAChange = async (token) => {
    clearVerifyTimeout();

    if (!token) {
      if (!isMountedRef.current) {
        return;
      }

      setStatus((currentStatus) =>
        currentStatus === "verifying" ? "idle" : currentStatus,
      );

      try {
        recaptchaRef.current?.reset?.();
      } catch {}

      return;
    }

    if (!isMountedRef.current) {
      return;
    }

    setStatus("sending");
    setErrorMessage("");

    const requestController = new AbortController();
    const requestTimeout = window.setTimeout(
      () => requestController.abort(),
      contactRequestTimeoutMs,
    );
    contactRequestRef.current = requestController;

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(buildPayload(token)),
        signal: requestController.signal,
      });

      const payload = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(
          payload?.details
            ? `${payload?.message || "Request failed"} (${payload.details})`
            : payload?.message || "Request failed",
        );
      }

      if (!isMountedRef.current) {
        return;
      }

      pushLeadConversion({
        formId: sectionId,
        formName: normalizedFormType,
        formLocation: sectionId,
        enquiryType: selectedSource?.source_name || undefined,
        leadId: payload?.leadReference || payload?.leadId || undefined,
        enhancedConversionData: {
          email: fields.email.trim(),
          phone_number: fullPhoneNumber.replace(/\s+/g, ""),
          first_name: fields.firstName.trim(),
          last_name: fields.lastName.trim(),
        },
      });

      setStatus("success");
      setErrorMessage("");
      setFields(initialFields);
    } catch (error) {
      if (isMountedRef.current) {
        const message =
          error?.name === "AbortError"
            ? "The enquiry request timed out. Please try again."
            : error?.message || "Something went wrong. Please try again.";

        setErrorMessage(
          message,
        );
        setStatus("error");
      }
    } finally {
      window.clearTimeout(requestTimeout);

      if (contactRequestRef.current === requestController) {
        contactRequestRef.current = null;
      }

      try {
        if (isMountedRef.current) {
          recaptchaRef.current?.reset?.();
        }
      } catch {}
    }
  };

  const onReCAPTCHAExpired = () => {
    clearVerifyTimeout();

    if (!isMountedRef.current) {
      return;
    }

    setErrorMessage("reCAPTCHA verification expired. Please try again.");
    setStatus((currentStatus) =>
      currentStatus === "verifying" ? "error" : currentStatus,
    );

    try {
      recaptchaRef.current?.reset?.();
    } catch {}
  };

  const onReCAPTCHAErrored = () => {
    clearVerifyTimeout();

    if (!isMountedRef.current) {
      return;
    }

    setErrorMessage("reCAPTCHA verification failed. Please try again.");
    setStatus((currentStatus) =>
      currentStatus === "verifying" ? "error" : currentStatus,
    );

    try {
      recaptchaRef.current?.reset?.();
    } catch {}
  };

  return (
    <section id={sectionId} className={sectionClasses}>
      <div className="mx-auto w-full max-w-328 grid gap-14 lg:grid-cols-3 lg:gap-12">
        <div className="space-y-8">
          <SectionEyebrow text={eyebrow} className="pt-0" />
          <SectionTitle text={title} className={introTitleClassName} />
          {introDescription ? (
            <p className={introBodyClassName}>
              <BlurFadeText
                text={introDescription}
                delay={0.08}
                blur={8}
                y={16}
              />
            </p>
          ) : null}

          <div className="space-y-4 pt-2">
            <div className="rounded-lg border border-black/10 p-10">
              <div className="flex items-start gap-5">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary text-base font-bold text-black">
                  1
                </span>
                <div className="min-w-0">
                  <p className="text-xl font-semibold leading-snug text-[#06040c]">
                    Submit your details
                  </p>
                  <p className="mt-1.5 text-sm leading-5 text-[#06040c]/62">
                    Fill in the form with your information and requirements.
                  </p>
                </div>
              </div>
            </div>
            <div className="rounded-lg border border-black/10 p-10">
              <div className="flex items-start gap-5">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary text-base font-bold text-black">
                  2
                </span>
                <div className="min-w-0">
                  <p className="text-xl font-semibold leading-snug text-[#06040c]">
                    We review your enquiry
                  </p>
                  <p className="mt-1.5 text-sm leading-5 text-[#06040c]/62">
                    The team triages your details and routes them to the most
                    relevant consultant.
                  </p>
                </div>
              </div>
            </div>
            <div className="rounded-lg border border-black/10 p-10">
              <div className="flex items-start gap-5">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary text-base font-bold text-black">
                  3
                </span>
                <div className="min-w-0">
                  <p className="text-xl font-semibold leading-snug text-[#06040c]">
                    A member of the team gets back to you
                  </p>
                  <p className="mt-1.5 text-sm leading-5 text-[#06040c]/62">
                    You receive a follow-up within one working day with a clear
                    next step tailored to your needs.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-2">
          <div className="flex min-h-[680px] flex-col items-center justify-center rounded-lg bg-primary p-2 sm:p-4 md:p-8 lg:p-16">
            <div className="w-full">
              {status === "success" ? (
                <div className="mx-auto max-w-lg space-y-8 text-center">
                  <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-white/30">
                    <FAIcon
                      icon="check"
                      size={20}
                      noMargin
                      className="text-[#06040c]"
                    />
                  </div>
                  <div className="space-y-3">
                    <h2 className="font-raleway text-[2rem] font-semibold leading-[0.96] tracking-[-0.05em] text-[#06040c]">
                      {successTitle}
                    </h2>
                    <p className="text-base leading-7 text-[#06040c]/70">
                      {successDescription}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={resetForm}
                    className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#06040c] px-7 py-3 text-sm font-semibold text-white transition-colors duration-200 hover:bg-[#06040c]/92"
                  >
                    {resetLabel}
                    <FAIcon icon="arrow-right" size={12} noMargin />
                  </button>
                </div>
              ) : (
                <div className={formCardClassName}>
                  <p className="font-semibold text-2xl">{formTitle}</p>
                  <p className="-mt-4 py-6">{formDescription}</p>

                  <form onSubmit={handleSubmit} className="space-y-5">
                    <input type="hidden" name="subject" value={subject} />
                    <input
                      type="hidden"
                      name="countryCode"
                      value={fields.countryCode}
                    />

                    <div className="grid gap-4 md:grid-cols-2">
                      <input
                        id="firstName"
                        type="text"
                        name="firstName"
                        value={fields.firstName}
                        onChange={handleChange}
                        autoComplete="given-name"
                        aria-label="First name"
                        placeholder="First name*"
                        className={inputClass}
                        required
                      />

                      <input
                        id="lastName"
                        type="text"
                        name="lastName"
                        value={fields.lastName}
                        onChange={handleChange}
                        autoComplete="family-name"
                        aria-label="Last name"
                        placeholder="Last name"
                        className={inputClass}
                      />
                    </div>

                    <div className="grid gap-4 md:grid-cols-[minmax(220px,0.72fr)_minmax(0,1.28fr)]">
                      <div className="relative" ref={countryMenuRef}>
                        <button
                          id="country"
                          type="button"
                          aria-label="Country"
                          aria-haspopup="listbox"
                          aria-expanded={isCountryMenuOpen}
                          aria-controls={countryListboxId}
                          className={countryTriggerClass}
                          onClick={() =>
                            setIsCountryMenuOpen((current) => !current)
                          }
                        >
                          <span className="truncate">
                            {selectedCountryLabel}
                          </span>
                          <span
                            className="shrink-0 text-[#06040c]/54"
                            aria-hidden="true"
                          >
                            <svg
                              viewBox="0 0 20 20"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                              className={`h-4 w-4 transition-transform duration-200 ${
                                isCountryMenuOpen ? "rotate-180" : ""
                              }`}
                            >
                              <path
                                d="M5 7.5L10 12.5L15 7.5"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </span>
                        </button>

                        {isCountryMenuOpen ? (
                          <div
                            id={countryListboxId}
                            role="listbox"
                            aria-labelledby="country"
                            data-lenis-prevent
                            className={countryMenuClass}
                          >
                            {countryOptions.map((option) => {
                              const isSelected =
                                option.isoCode === fields.countryCode;

                              return (
                                <button
                                  key={option.isoCode}
                                  type="button"
                                  role="option"
                                  aria-selected={isSelected}
                                  className={`${countryOptionClass} ${
                                    isSelected ? countryOptionSelectedClass : ""
                                  }`}
                                  onClick={() =>
                                    handleCountryChange(option.isoCode)
                                  }
                                >
                                  {option.displayLabel ?? option.label}
                                </button>
                              );
                            })}
                          </div>
                        ) : null}
                      </div>

                      <div className={phoneFieldShellClass}>
                        <span className={phonePrefixClass} aria-hidden="true">
                          {selectedCountry.dialCode}
                        </span>
                        <input
                          id="phoneNumber"
                          type="tel"
                          name="phoneNumber"
                          value={fields.phoneNumber}
                          onChange={handleChange}
                          autoComplete="tel-national"
                          inputMode="tel"
                          aria-label="Phone number"
                          placeholder="Phone number"
                          className={phoneInputClass}
                        />
                      </div>
                    </div>

                    <div className="grid gap-4 md:grid-cols-2">
                      <input
                        id="email"
                        type="email"
                        name="email"
                        value={fields.email}
                        onChange={handleChange}
                        autoComplete="email"
                        inputMode="email"
                        aria-label="Email address"
                        placeholder="Email address*"
                        className={inputClass}
                        required
                      />

                      <input
                        id="companyName"
                        type="text"
                        name="companyName"
                        value={fields.companyName}
                        onChange={handleChange}
                        autoComplete="organization"
                        aria-label="Company name"
                        placeholder="Company name"
                        className={inputClass}
                      />
                    </div>

                    <div className="space-y-2">
                      <input
                        type="hidden"
                        name="sourceId"
                        value={fields.sourceId}
                      />

                      <div className="relative" ref={sourceMenuRef}>
                        <button
                          id="source"
                          type="button"
                          aria-label="How did you hear about us"
                          aria-haspopup="listbox"
                          aria-expanded={isSourceMenuOpen}
                          aria-controls={sourceListboxId}
                          className={countryTriggerClass}
                          onClick={() => {
                            if (sourceStatus !== "ready") {
                              return;
                            }

                            setIsSourceMenuOpen((current) => !current);
                          }}
                          disabled={sourceStatus !== "ready"}
                        >
                          <span className="truncate">
                            {selectedSourceLabel}
                          </span>
                          <span
                            className="shrink-0 text-[#06040c]/54"
                            aria-hidden="true"
                          >
                            <svg
                              viewBox="0 0 20 20"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                              className={`h-4 w-4 transition-transform duration-200 ${
                                isSourceMenuOpen ? "rotate-180" : ""
                              }`}
                            >
                              <path
                                d="M5 7.5L10 12.5L15 7.5"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </span>
                        </button>

                        {isSourceMenuOpen ? (
                          <div
                            id={sourceListboxId}
                            role="listbox"
                            aria-labelledby="source"
                            data-lenis-prevent
                            className={countryMenuClass}
                          >
                            {sourceOptions.map((option) => {
                              const isSelected =
                                String(option.source_id) === fields.sourceId;

                              return (
                                <button
                                  key={option.source_id}
                                  type="button"
                                  role="option"
                                  aria-selected={isSelected}
                                  className={`${countryOptionClass} ${
                                    isSelected ? countryOptionSelectedClass : ""
                                  }`}
                                  onClick={() =>
                                    handleSourceChange(option.source_id)
                                  }
                                >
                                  {option.source_name}
                                </button>
                              );
                            })}
                          </div>
                        ) : null}
                      </div>

                      {sourceStatus === "error" ? (
                        <p className="text-sm text-red-600">
                          Enquiry sources could not be loaded from Nexus. Check
                          the public sources endpoint configuration.
                        </p>
                      ) : null}

                      {sourceStatus === "empty" ? (
                        <p className="text-sm text-amber-700">
                          No website-visible sources are active yet. Enable at
                          least one `source_website` source in Nexus.
                        </p>
                      ) : null}
                    </div>

                    <div
                      className={`space-y-3 rounded-md p-5 text-sm leading-5 text-[#06040c]/68 ${
                        embedded ? "bg-[#06040c]/[0.035]" : ""
                      }`}
                    >
                      <p>
                        Safety Management Limited values your privacy and will
                        only use your information to manage your enquiry and
                        provide the requested services. If you would like to
                        hear about future updates, tick the box below.
                      </p>

                      <label className="flex items-start gap-3 text-[#06040c]">
                        <input
                          type="checkbox"
                          name="marketingConsent"
                          checked={fields.marketingConsent}
                          onChange={handleChange}
                          className="mt-1 h-4 w-4 rounded border border-black/20 accent-[#06040c]"
                        />
                        <span>
                          I agree to receive other communications from Safety
                          Management Limited.
                        </span>
                      </label>

                      <p className="text-[#06040c]/54">
                        You can unsubscribe from these communications at any
                        time.
                      </p>
                      <p className="text-[#06040c]/54">
                        By clicking submit, you consent to Safety Management
                        Limited storing and processing your personal data to
                        provide the requested content.
                      </p>
                    </div>

                    {recaptchaSiteKey ? (
                      <Recaptcha
                        ref={recaptchaRef}
                        size="invisible"
                        sitekey={recaptchaSiteKey}
                        onChange={onReCAPTCHAChange}
                        onExpired={onReCAPTCHAExpired}
                        onErrored={onReCAPTCHAErrored}
                      />
                    ) : null}

                    {status === "config-error" ? (
                      <p className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
                        Contact form setup is incomplete. Add{" "}
                        NEXT_PUBLIC_RECAPTCHA_SITE_KEY to your environment and
                        restart the app.
                      </p>
                    ) : null}

                    {status === "error" ? (
                      <p className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-center w-full text-red-600">
                        {errorMessage ||
                          "Something went wrong. Please try again or call us on"}
                        {!errorMessage ? (
                          <>
                            <a
                              href="tel:+441327311300"
                              className="ml-1 font-semibold underline"
                            >
                              +44 (0)1327 311300
                            </a>
                            .
                          </>
                        ) : null}
                      </p>
                    ) : null}

                    <div className="flex flex-col items-center gap-4 border-t border-black/8 pt-3 text-center">
                      <a
                        href="/privacy-and-cookies"
                        className="text-sm font-medium text-[#06040c]/52 transition-colors duration-200 hover:text-[#06040c]"
                      >
                        Read our Privacy & Cookies Policy
                      </a>

                      <button
                        type="submit"
                        disabled={
                          isSubmitting || !recaptchaSiteKey || !isFormValid
                        }
                        className={`inline-flex h-14 items-center justify-center gap-2 rounded-lg bg-[#06040c] px-8 text-base font-semibold text-white transition-colors duration-200 hover:bg-[#06040c]/92 disabled:cursor-not-allowed disabled:opacity-60 ${
                          embedded ? "w-full" : ""
                        }`}
                      >
                        {isSubmitting ? (
                          <>
                            <FAIcon icon="rotate" spin size={12} noMargin />
                            {status === "verifying" ? "Verifying" : "Sending"}
                          </>
                        ) : (
                          <>
                            {submitLabel}
                            <FAIcon icon="arrow-right" size={12} noMargin />
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
